import React, { useState, useEffect, useContext } from "react";
import Select from "react-select";
import "./Estimate.css";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  getSketchDesignEstimate,
  addSketchDesignEstimateWorkType,
  updateSketchDesignEstimateWorkType,
  deleteSketchDesignEstimateWorkType,
  getSketchDesignEstimateWorkTypeNames,
  updateSketchDesignEstimate,
  getSketchDesignApproval,
  createSketchDesignApproval,
  deleteSketchDesignApproval,
  updateSketchDesignApproverStatus,
} from "../../../../api/projectEstimatesApi";
import { getSketchDesignProjectById } from "../../../../api/projectApi";
import CommercialOfferApprovalModal from "../../ProjectFinance/CommercialOffer/CommercialOfferApprovalModal";
import Preloader from "../../../Preloader/Preloader";
import CoinIcon from "../../../../images/projects/ProjectFinance/Coinssvg.svg";
import CalcIcon from "../../../../images/projects/ProjectFinance/Calc.svg";
import SigmaIcon from "../../../../images/projects/ProjectFinance/Sigma1.svg";

const CREATE_NEW_OPTION = "__create_new__";

const selectStyles = {
  container: (base) => ({ ...base, minWidth: 260 }),
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderColor: state.isFocused ? "#1976d2" : "#dadce0",
    boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : "none",
    "&:hover": {
      borderColor: "#1976d2",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e3f2fd",
    borderRadius: "4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#1976d2",
  }),
  multiValueRemove: (base) => ({
    ...base,
    "&:hover": {
      backgroundColor: "#1976d2",
      color: "white",
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#e3f2fd"
      : state.isFocused
        ? "#f5f5f5"
        : "white",
    color: "#202124",
    "&:active": {
      backgroundColor: "#e3f2fd",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10,
  }),
};

const formatMoney = (value) =>
  (value ?? 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getApprovalLockMessage = (
  estimateOnApproval,
  commercialOfferOnApproval,
) => {
  if (estimateOnApproval && commercialOfferOnApproval) {
    return "Редактирование запрещено, так как смета и коммерческое предложение находятся на этапе согласования";
  }
  if (estimateOnApproval) {
    return "Редактирование запрещено, так как смета находится на этапе согласования";
  }
  if (commercialOfferOnApproval) {
    return "Редактирование запрещено, так как коммерческое предложение находится на этапе согласования";
  }
  return null;
};

const Estimate = ({
  projectId,
  projectLockedByApproval = false,
  estimateOnApproval = false,
  commercialOfferOnApproval = false,
  onApprovalStateChange,
}) => {
  const { userData } = useContext(UserContext);
  const isEditable = !projectLockedByApproval;
  const lockBannerMessage = getApprovalLockMessage(
    estimateOnApproval,
    commercialOfferOnApproval,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [estimateNumber, setEstimateNumber] = useState("001");
  const [estimateDate, setEstimateDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [editingEstimateNumber, setEditingEstimateNumber] = useState(false);
  const [editingEstimateDate, setEditingEstimateDate] = useState(false);
  const [addWorkTypeModalOpen, setAddWorkTypeModalOpen] = useState(false);
  const [addWorkTypeSelected, setAddWorkTypeSelected] = useState([]);
  const [addWorkTypeNewName, setAddWorkTypeNewName] = useState("");
  const [addWorkTypeSubmitting, setAddWorkTypeSubmitting] = useState(false);
  const [workTypeNamesList, setWorkTypeNamesList] = useState([]);
  const [workTypeNamesLoading, setWorkTypeNamesLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [approvalData, setApprovalData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [isApproverActionLoading, setIsApproverActionLoading] = useState(false);
  const [directThresholdPercentApplied, setDirectThresholdPercentApplied] =
    useState("40");
  const [directThresholdPercentDraft, setDirectThresholdPercentDraft] =
    useState("40");
  const [isDirectThresholdEditing, setIsDirectThresholdEditing] = useState(false);
  const [isDirectThresholdSubmitting, setIsDirectThresholdSubmitting] =
    useState(false);

  const refetchEstimate = async () => {
    if (!projectId) return;
    try {
      const result = await getSketchDesignEstimate(projectId);
      setData(result);
    } catch (err) {
      console.error("Ошибка обновления сметы:", err);
    }
  };

  useEffect(() => {
    const fetchEstimate = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [result, approval, project] = await Promise.all([
          getSketchDesignEstimate(projectId),
          getSketchDesignApproval(projectId, "estimate"),
          getSketchDesignProjectById(projectId),
        ]);
        setData(result);
        setApprovalData(approval ?? null);
        setProjectData(project ?? null);
      } catch (err) {
        console.error("Ошибка загрузки сметы:", err);
        setError(err?.message || "Не удалось загрузить смету");
      } finally {
        setLoading(false);
      }
    };
    fetchEstimate();
  }, [projectId]);

  const total = data?.contract_amount_total ?? 0;
  const directExpenses = data?.direct_expenses ?? 0;
  const clearProfit = data?.clear_profit ?? 0;
  const maxDirectExpenses = data?.max_direct_expenses ?? 0;
  const withoutNds = data?.contract_amount_total_without_nds ?? 0;
  const currentNds = data?.current_nds ?? 0;
  const ndsSum = total - withoutNds;

  const directPercent =
    total > 0 ? ((directExpenses / total) * 100).toFixed(0) : "0";
  const profitPercent =
    total > 0 ? ((clearProfit / total) * 100).toFixed(0) : "0";
  const maxDirectPercentage = data?.max_direct_percentage ?? 40;

  useEffect(() => {
    if (!Number.isFinite(maxDirectPercentage)) {
      setDirectThresholdPercentApplied("40");
      setDirectThresholdPercentDraft("40");
      return;
    }
    const normalized =
      Math.round(Math.max(maxDirectPercentage, 0) * 100) / 100;
    const formatted = String(normalized);
    setDirectThresholdPercentApplied(formatted);
    setDirectThresholdPercentDraft(formatted);
  }, [maxDirectPercentage]);

  const parsePercentValue = (rawValue) => {
    const parsed = Number.parseFloat(String(rawValue).replace(",", "."));
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(parsed, 0);
  };

  const formatPercentDisplay = (value) => {
    if (!Number.isFinite(value)) return "0";
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2).replace(/\.?0+$/, "");
  };

  const thresholdPercentValue = parsePercentValue(directThresholdPercentApplied);
  const canEditDirectThreshold =
    isEditable &&
    userData?.access_levels?.some((level) =>
      ["РО", "ФИН", "АДМС"].includes(level?.name),
    );

  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return `${d}.${m}.${y}`;
  };

  if (!projectId) {
    return (
      <div className="sd-estimate">
        <div className="sd-estimate-error">Ошибка: ID проекта не передан</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sd-estimate">
        <Preloader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-estimate">
        <div className="sd-estimate-error">Ошибка: {error}</div>
      </div>
    );
  }

  const workTypes = data?.work_types ?? [];
  const workTypesSorted = [...workTypes].sort(
    (a, b) => (a.id ?? 0) - (b.id ?? 0),
  );
  const existingNames = workTypes
    .map((w) => (w.name || "").trim().toLowerCase())
    .filter(Boolean);

  const availableWorkTypeOptions = workTypeNamesList
    .filter((w) => !existingNames.includes((w.name || "").trim().toLowerCase()))
    .map((w) => ({ value: w.name, label: w.name }));

  const isCreateNew =
    addWorkTypeSelected.length === 1 &&
    addWorkTypeSelected[0]?.value === CREATE_NEW_OPTION;
  const newNameTrimmed = addWorkTypeNewName.trim();
  const isDuplicateName =
    isCreateNew &&
    newNameTrimmed &&
    existingNames.includes(newNameTrimmed.toLowerCase());
  const canAddWorkType = isCreateNew
    ? newNameTrimmed && !isDuplicateName
    : addWorkTypeSelected.length > 0 &&
      !addWorkTypeSelected.some((opt) => opt?.value === CREATE_NEW_OPTION);

  const openAddModal = async () => {
    setAddWorkTypeSelected([]);
    setAddWorkTypeNewName("");
    setAddWorkTypeModalOpen(true);
    setWorkTypeNamesLoading(true);
    try {
      const names = await getSketchDesignEstimateWorkTypeNames();
      setWorkTypeNamesList(names || []);
    } catch (err) {
      console.error("Ошибка загрузки списка видов работ:", err);
      setWorkTypeNamesList([]);
    } finally {
      setWorkTypeNamesLoading(false);
    }
  };

  const closeAddModal = () => {
    setAddWorkTypeModalOpen(false);
  };

  const validateNewName = () => {
    // Валидация дубликата отображается через isDuplicateName в UI
  };

  const handleAddWorkType = async () => {
    if (!isEditable || !canAddWorkType || addWorkTypeSubmitting || !projectId)
      return;

    const namesToAdd = isCreateNew
      ? [newNameTrimmed]
      : addWorkTypeSelected
          .filter((opt) => opt?.value !== CREATE_NEW_OPTION)
          .map((opt) => opt.value);

    if (namesToAdd.length === 0) return;
    if (isCreateNew && existingNames.includes(namesToAdd[0].toLowerCase()))
      return;

    setAddWorkTypeSubmitting(true);
    try {
      for (const nameToAdd of namesToAdd) {
        await addSketchDesignEstimateWorkType(projectId, {
          name: nameToAdd,
          hour_rate: 0,
          labor_costs: 0,
          final_sum: 0,
        });
      }
      await refetchEstimate();
      closeAddModal();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Ошибка добавления вида работ");
    } finally {
      setAddWorkTypeSubmitting(false);
    }
  };

  const handleDeleteWorkType = async (id) => {
    if (!isEditable || !id || !window.confirm("Удалить вид работ?")) return;
    setActionLoadingId(id);
    try {
      await deleteSketchDesignEstimateWorkType(id);
      await refetchEstimate();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Ошибка удаления");
    } finally {
      setActionLoadingId(null);
    }
  };

  const totalShare = workTypes.reduce(
    (s, w) => s + (Number(w.share_percent) || 0),
    0,
  );
  const totalMiddleSalary = workTypes.reduce(
    (s, w) => s + (Number(w.middle_salary) || 0),
    0,
  );
  const totalLabor = workTypes.reduce(
    (s, w) => s + (Number(w.labor_costs) || 0),
    0,
  );
  const totalDirect = workTypes.reduce(
    (s, w) => s + (Number(w.final_sum) || 0),
    0,
  );

  const buildWorkTypeUpdateBody = (row, overrides = {}) => ({
    name: row.name ?? "",
    middle_salary: Number(row.middle_salary) || 0,
    labor_costs: Number(row.labor_costs) || 0,
    already_payed: Number(row.already_payed) || 0,
    status: row.status ?? "in_processing",
    ...overrides,
  });

  const getStatusIcon = (status) => {
    if (!status)
      return (
        <span className="sd-estimate-approval-status-icon pending">⏳</span>
      );
    const s = String(status).toLowerCase();
    if (s === "согласован" || s === "approved")
      return (
        <span className="sd-estimate-approval-status-icon approved">✓</span>
      );
    if (s === "на рассмотрении" || s === "pending")
      return (
        <span className="sd-estimate-approval-status-icon pending">⏳</span>
      );
    if (s === "не согласован" || s === "rejected")
      return (
        <span className="sd-estimate-approval-status-icon rejected">✕</span>
      );
    return <span className="sd-estimate-approval-status-icon pending">⏳</span>;
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const s = String(status).toLowerCase();
    if (s === "согласован" || s === "approved") return "Согласован";
    if (s === "на рассмотрении" || s === "pending") return "На рассмотрении";
    if (s === "не согласован" || s === "rejected") return "Отклонен";
    return "На рассмотрении";
  };

  const toNumericApproverIds = (checked) =>
    checked
      .map((id) => (typeof id === "number" ? id : parseInt(id, 10)))
      .filter((id) => Number.isInteger(id) && !Number.isNaN(id));

  const handleApprovalSubmit = async (checkedIds) => {
    const ids = toNumericApproverIds(checkedIds);
    if (!projectId || ids.length === 0) return;
    setIsSubmittingApproval(true);
    try {
      await createSketchDesignApproval(projectId, "estimate", ids);
      const updated = await getSketchDesignApproval(projectId, "estimate");
      setApprovalData(updated ?? null);
      setShowApprovalModal(false);
      onApprovalStateChange?.();
      alert("Смета успешно отправлена на согласование.");
    } catch (err) {
      console.error("Ошибка при отправке сметы на согласование:", err);
      alert(err?.message || "Ошибка при отправке на согласование.");
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const handleDeleteApproval = async () => {
    if (!projectId || !window.confirm("Удалить согласование по смете?")) return;
    setIsDeletingApproval(true);
    try {
      await deleteSketchDesignApproval(projectId, "estimate");
      setApprovalData(null);
      onApprovalStateChange?.();
      alert("Согласование удалено.");
    } catch (err) {
      console.error("Ошибка при удалении согласования:", err);
      alert(err?.message || "Ошибка при удалении согласования.");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  const myApprover = approvalData?.approvers?.find(
    (a) =>
      a.user?.id === userData?.id || String(a.user_id) === String(userData?.id),
  );
  const isMyApprovalPending =
    myApprover &&
    ["in_processing", "pending", "на рассмотрении"].includes(
      String(myApprover.status || "").toLowerCase(),
    );

  const handleApproverSetStatus = async (status) => {
    if (!myApprover?.id || !projectId) return;
    setIsApproverActionLoading(true);
    try {
      await updateSketchDesignApproverStatus(myApprover.id, status);
      const updated = await getSketchDesignApproval(projectId, "estimate");
      setApprovalData(updated ?? null);
      alert(status === "approved" ? "Смета согласована." : "Смета отклонена.");
    } catch (err) {
      console.error("Ошибка при обновлении статуса:", err);
      alert(err?.message || "Ошибка при обновлении статуса");
    } finally {
      setIsApproverActionLoading(false);
    }
  };

  const handleDirectThresholdChange = (e) => {
    const value = e.target.value.replace(",", ".");
    if (!/^\d*\.?\d*$/.test(value)) return;
    setDirectThresholdPercentDraft(value);
  };

  const handleDirectThresholdFocus = (e) => {
    e.target.select();
    setIsDirectThresholdEditing(true);
  };

  const handleDirectThresholdApply = async () => {
    if (!projectId) return;
    const normalizedValue = formatPercentDisplay(
      parsePercentValue(directThresholdPercentDraft),
    );
    setIsDirectThresholdSubmitting(true);
    try {
      await updateSketchDesignEstimate(projectId, {
        contract_amount_total: Number(data?.contract_amount_total) || 0,
        contract_amount_total_without_nds:
          Number(data?.contract_amount_total_without_nds) || 0,
        direct_expenses: Number(data?.direct_expenses) || 0,
        clear_profit: Number(data?.clear_profit) || 0,
        max_direct_percentage: parsePercentValue(normalizedValue),
        max_direct_expenses: Number(data?.max_direct_expenses) || 0,
        deviations: Number(data?.deviations) || 0,
      });
      await refetchEstimate();
      setDirectThresholdPercentApplied(normalizedValue);
      setDirectThresholdPercentDraft(normalizedValue);
      setIsDirectThresholdEditing(false);
    } catch (err) {
      console.error("Ошибка обновления порога прямых затрат:", err);
      alert(err?.message || "Не удалось обновить порог прямых затрат");
    } finally {
      setIsDirectThresholdSubmitting(false);
    }
  };

  const handleDirectThresholdCancel = () => {
    setDirectThresholdPercentDraft(directThresholdPercentApplied);
    setIsDirectThresholdEditing(false);
  };

  return (
    <div className="sd-estimate">
      {projectLockedByApproval && lockBannerMessage && (
        <div className="sd-estimate-lock-banner" role="alert">
          <span className="sd-estimate-lock-banner-icon">⚠</span>
          {lockBannerMessage}
        </div>
      )}
      {/* Блок 1: Результаты расчета сметы */}
      <section className="sd-estimate-block sd-estimate-block-results">
        <div className="sd-estimate-block-results-top-bar">
          <h2 className="sd-estimate-block-title">Результаты расчета сметы</h2>
          {approvalData?.approvers?.length > 0 ? (
            <div className="sd-estimate-approval-with-actions">
              <div className="sd-estimate-approval-status-block">
                {approvalData.approvers.map((approver, index) => {
                  const name = approver.user
                    ? [approver.user.surname, approver.user.name]
                        .filter(Boolean)
                        .join(" ")
                        .trim()
                    : "Не указан";
                  return (
                    <div
                      key={approver.id ?? index}
                      className="sd-estimate-approval-step"
                    >
                      <span className="sd-estimate-approver-name">
                        {name || "Не указан"}:
                      </span>
                      {getStatusIcon(approver.status)}
                      <span className="sd-estimate-step-status-text">
                        {getStatusText(approver.status)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="sd-estimate-btn-delete-approval"
                onClick={handleDeleteApproval}
                disabled={isDeletingApproval}
              >
                {isDeletingApproval ? "Удаление..." : "Удалить согласование"}
              </button>
            </div>
          ) : (
            <div className="sd-estimate-approval-empty-row">
              <div className="sd-estimate-approval-empty">
                <span className="sd-estimate-approval-empty-icon">ℹ️</span>
                Согласований по смете пока нет
              </div>
              <button
                type="button"
                className="sd-estimate-btn-send-approval"
                onClick={() => setShowApprovalModal(true)}
                disabled={isSubmittingApproval}
              >
                {isSubmittingApproval
                  ? "Отправка..."
                  : "Отправить на согласование"}
              </button>
            </div>
          )}
        </div>
        <div className="sd-estimate-inputs-row">
          <div className="sd-estimate-input-group">
            <label>№ сметы</label>
            {editingEstimateNumber && isEditable ? (
              <span className="sd-estimate-editable-row">
                <input
                  type="text"
                  value={estimateNumber}
                  onChange={(e) => setEstimateNumber(e.target.value)}
                  onBlur={() => setEditingEstimateNumber(false)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setEditingEstimateNumber(false)
                  }
                  className="sd-estimate-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="sd-estimate-edit-done"
                  onClick={() => setEditingEstimateNumber(false)}
                  aria-label="Готово"
                >
                  ✓
                </button>
              </span>
            ) : (
              <span className="sd-estimate-display-row">
                <span className="sd-estimate-display-value">
                  {estimateNumber}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    className="sd-estimate-pencil"
                    onClick={() => setEditingEstimateNumber(true)}
                    title="Редактировать"
                    aria-label="Редактировать"
                  >
                    ✎
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="sd-estimate-input-group">
            <label>Введите дату</label>
            {editingEstimateDate && isEditable ? (
              <span className="sd-estimate-editable-row">
                <input
                  type="date"
                  value={estimateDate}
                  onChange={(e) => setEstimateDate(e.target.value)}
                  onBlur={() => setEditingEstimateDate(false)}
                  className="sd-estimate-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="sd-estimate-edit-done"
                  onClick={() => setEditingEstimateDate(false)}
                  aria-label="Готово"
                >
                  ✓
                </button>
              </span>
            ) : (
              <span className="sd-estimate-display-row">
                <span className="sd-estimate-date-icon" aria-hidden>
                  📅
                </span>
                <span className="sd-estimate-display-value">
                  {formatDisplayDate(estimateDate)}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    className="sd-estimate-pencil"
                    onClick={() => setEditingEstimateDate(true)}
                    title="Редактировать"
                    aria-label="Редактировать"
                  >
                    ✎
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Блок 2: План рентабельности — четыре карточки */}
      <section className="sd-estimate-block sd-estimate-block-plan">
        <h2 className="sd-estimate-block-title">План рентабельности проекта</h2>
        <div className="sd-estimate-cards-row">
          <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-blue">
              <img src={CoinIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">Доход</div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(total)}
            </div>
            <div className="sd-estimate-card-sub">100%</div>
          </div>
          <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-yellow">
              <img src={CalcIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">Расходы прямые</div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(directExpenses)}
            </div>
            <div className="sd-estimate-card-sub">{directPercent}%</div>
          </div>
          <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-green">
              <img src={SigmaIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">Валовая прибыль</div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(clearProfit)}
            </div>
            <div className="sd-estimate-card-sub">{profitPercent}%</div>
          </div>
          {/* <div className="sd-estimate-card">
            <div className="sd-estimate-card-icon sd-estimate-card-icon-target">
              <img src={TargetIcon} alt="" />
            </div>
            <div className="sd-estimate-card-label">
              Нормативная чистая прибыль за отделение
            </div>
            <div className="sd-estimate-card-value">
              〒 {formatMoney(clearProfit)}
            </div>
            <div className="sd-estimate-card-sub">%14</div>
          </div> */}
        </div>

        {/* Два блока сумм */}
        <div className="sd-estimate-summary-blocks">
          <div className="sd-estimate-summary-block">
            <div className="sd-estimate-summary-block-label">
              СУММА ОСНОВНОГО ДОГОВОРА
            </div>
            <div className="sd-estimate-summary-block-value">
              〒 {formatMoney(total)}
            </div>
          </div>
          <div className="sd-estimate-summary-block">
            <div className="sd-estimate-summary-block-label">
              СУММА ЗА ВЫЧЕТОМ НДС {currentNds}%
            </div>
            <div className="sd-estimate-summary-block-value">
              〒 {formatMoney(withoutNds)}
            </div>
            <div className="sd-estimate-summary-block-nds">
              Сумма НДС: {formatMoney(ndsSum)} 〒
            </div>
          </div>
        </div>

        {/* Список: порог, затраты, отклонения */}
        <ul className="sd-estimate-metrics-list">
          <li className="sd-estimate-metrics-item">
            <span className="sd-estimate-metrics-bullet sd-estimate-metrics-bullet-blue" />
            <span className="sd-estimate-metrics-label">
              Порог прямых затрат
            </span>
            <span className="sd-estimate-metrics-threshold-control">
              {canEditDirectThreshold ? (
                <>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="sd-estimate-threshold-input"
                    value={directThresholdPercentDraft}
                    onChange={handleDirectThresholdChange}
                    onFocus={handleDirectThresholdFocus}
                    aria-label="Процент порога прямых затрат"
                  />
                  <span className="sd-estimate-threshold-unit">%</span>
                  {isDirectThresholdEditing && (
                    <span className="sd-estimate-threshold-actions" role="group">
                      <button
                        type="button"
                        className="sd-estimate-threshold-action sd-estimate-threshold-action-apply"
                        onClick={handleDirectThresholdApply}
                        disabled={isDirectThresholdSubmitting}
                        title="Применить"
                        aria-label="Применить"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="sd-estimate-threshold-action sd-estimate-threshold-action-cancel"
                        onClick={handleDirectThresholdCancel}
                        disabled={isDirectThresholdSubmitting}
                        title="Отменить"
                        aria-label="Отменить"
                      >
                        ✕
                      </button>
                    </span>
                  )}
                </>
              ) : (
                <span className="sd-estimate-threshold-readonly">
                  ({formatPercentDisplay(maxDirectPercentage)}%)
                </span>
              )}
            </span>
            <span className="sd-estimate-metrics-value">
              〒 {formatMoney(maxDirectExpenses)}
            </span>
          </li>
          <li className="sd-estimate-metrics-item">
            <span className="sd-estimate-metrics-bullet sd-estimate-metrics-bullet-green" />
            <span className="sd-estimate-metrics-label">
              Рассчитываемые затраты (ПЗ)
            </span>
            <span className="sd-estimate-metrics-value">
              〒 {formatMoney(directExpenses)}
            </span>
          </li>
          <li className="sd-estimate-metrics-item">
            <span className="sd-estimate-metrics-bullet sd-estimate-metrics-bullet-green" />
            <span className="sd-estimate-metrics-label">Отклонения (+,-)</span>
            <span
              className={`sd-estimate-metrics-value sd-estimate-deviation ${(data?.deviations ?? 0) >= 0 ? "sd-estimate-deviation-plus" : "sd-estimate-deviation-minus"}`}
            >
              〒 {(data?.deviations ?? 0) >= 0 ? "+" : ""}
              {formatMoney(data?.deviations ?? 0)}
            </span>
          </li>
        </ul>
      </section>

      {/* Блок 3: Виды работ */}
      <section className="sd-estimate-block sd-estimate-block-work-types">
        <div className="sd-estimate-work-types-header">
          <h2 className="sd-estimate-block-title">Вид работ</h2>
          <button
            type="button"
            className="sd-estimate-btn-add"
            onClick={openAddModal}
            disabled={!isEditable}
          >
            <span className="sd-estimate-btn-add-icon">+</span>
            Добавить вид работ
          </button>
        </div>
        <div className="sd-estimate-table-wrap">
          <table className="sd-estimate-table">
            <thead>
              <tr>
                <th>Вид работ</th>
                <th>
                  % доля в ЭП{" "}
                  <span
                    className="sd-estimate-th-info"
                    title="Доля в эскизном проекте"
                  >
                    ?
                  </span>
                </th>
                <th>
                  Средняя зарплата{" "}
                  <span
                    className="sd-estimate-th-info"
                    title="Средняя зарплата"
                  >
                    ?
                  </span>
                </th>
                <th>
                  Трудозатраты (ч.){" "}
                  <span className="sd-estimate-th-info" title="Часы">
                    ?
                  </span>
                </th>
                <th>
                  Прямые затраты{" "}
                  <span className="sd-estimate-th-info" title="Прямые затраты">
                    ?
                  </span>
                </th>
                <th className="sd-estimate-th-actions">Действия</th>
              </tr>
            </thead>
            <tbody>
              {workTypesSorted.length > 0 ? (
                <>
                  {workTypesSorted.map((row) => (
                    <tr key={row.id}>
                      <td className="sd-estimate-cell-text">
                        {row.name ?? "—"}
                      </td>
                      <td>
                        <span className="sd-estimate-cell-value">
                          {row.share_percent != null
                            ? `${Number(row.share_percent).toLocaleString("ru-RU")}%`
                            : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="sd-estimate-cell-edit">
                          <span className="sd-estimate-cell-currency">〒</span>
                          <input
                            type="text"
                            defaultValue={
                              row.middle_salary != null ? row.middle_salary : ""
                            }
                            className="sd-estimate-cell-input"
                            readOnly={!isEditable}
                            onFocus={(e) => {
                              if (isEditable) {
                                const input = e.target;
                                requestAnimationFrame(() => input.select());
                              }
                            }}
                            onBlur={(e) => {
                              if (!isEditable) return;
                              const v = e.target.value
                                .replace(/\s/g, "")
                                .replace(",", ".");
                              const num = v === "" ? 0 : parseFloat(v);
                              if (!Number.isNaN(num) && num >= 0 && row.id) {
                                updateSketchDesignEstimateWorkType(
                                  row.id,
                                  buildWorkTypeUpdateBody(row, {
                                    middle_salary: num,
                                  }),
                                )
                                  .then(refetchEstimate)
                                  .catch(console.error);
                              }
                            }}
                          />
                          {isEditable && (
                            <button
                              type="button"
                              className="sd-estimate-pencil"
                              aria-label="Редактировать"
                            >
                              ✎
                            </button>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="sd-estimate-cell-edit">
                          <input
                            type="text"
                            defaultValue={
                              row.labor_costs != null ? row.labor_costs : ""
                            }
                            className="sd-estimate-cell-input"
                            readOnly={!isEditable}
                            onFocus={(e) => {
                              if (isEditable) {
                                const input = e.target;
                                requestAnimationFrame(() => input.select());
                              }
                            }}
                            onBlur={(e) => {
                              if (!isEditable) return;
                              const v = e.target.value;
                              const num =
                                v === "" ? 0 : parseFloat(v.replace(",", "."));
                              if (!Number.isNaN(num) && num >= 0 && row.id) {
                                updateSketchDesignEstimateWorkType(
                                  row.id,
                                  buildWorkTypeUpdateBody(row, {
                                    labor_costs: num,
                                  }),
                                )
                                  .then(refetchEstimate)
                                  .catch(console.error);
                              }
                            }}
                          />
                          {isEditable && (
                            <button
                              type="button"
                              className="sd-estimate-pencil"
                              aria-label="Редактировать"
                            >
                              ✎
                            </button>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className="sd-estimate-cell-value">
                          〒 {formatMoney(row.final_sum ?? 0)}
                        </span>
                      </td>
                      <td className="sd-estimate-cell-actions">
                        <button
                          type="button"
                          className="sd-estimate-btn-delete"
                          aria-label="Удалить"
                          onClick={() => handleDeleteWorkType(row.id)}
                          disabled={!isEditable || actionLoadingId === row.id}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="sd-estimate-table-total">
                    <td>
                      <strong>ИТОГО</strong>
                    </td>
                    <td>
                      <strong>{totalShare.toLocaleString("ru-RU")}%</strong>
                    </td>
                    <td>
                      <strong>〒 {formatMoney(totalMiddleSalary)}</strong>
                    </td>
                    <td>
                      <strong>{totalLabor.toLocaleString("ru-RU")}</strong>
                    </td>
                    <td>
                      <strong>〒 {formatMoney(totalDirect)}</strong>
                    </td>
                    <td />
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={6} className="sd-estimate-table-empty">
                    Нет видов работ. Нажмите «Добавить вид работ».
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Модальное окно добавления вида работ */}
      {addWorkTypeModalOpen && (
        <div className="sd-estimate-modal-overlay" onClick={closeAddModal}>
          <div
            className="sd-estimate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="sd-estimate-modal-title">
              Добавление нового вида работ
            </h3>
            <div className="sd-estimate-modal-field">
              <label>Выберите виды работ из списка*</label>
              {workTypeNamesLoading ? (
                <div className="sd-estimate-modal-loading">Загрузка...</div>
              ) : (
                <>
                  {availableWorkTypeOptions.length === 0 &&
                    workTypeNamesList.length === 0 && (
                      <div className="sd-estimate-modal-all-added">
                        Список видов работ с сервера пуст. Выберите «Создать
                        новый вид работ» ниже и введите название.
                      </div>
                    )}
                  {availableWorkTypeOptions.length === 0 &&
                    workTypeNamesList.length > 0 && (
                      <div className="sd-estimate-modal-all-added">
                        Все доступные виды работ уже добавлены в проект. Можете
                        создать новый вид работ ниже.
                      </div>
                    )}
                  <Select
                    isMulti
                    options={[
                      ...availableWorkTypeOptions,
                      {
                        value: CREATE_NEW_OPTION,
                        label: "+ Создать новый вид работ",
                      },
                    ]}
                    value={addWorkTypeSelected}
                    onChange={(selectedOptions) => {
                      const selected = selectedOptions || [];
                      const hasCreateNew = selected.some(
                        (opt) => opt.value === CREATE_NEW_OPTION,
                      );
                      if (hasCreateNew) {
                        setAddWorkTypeSelected([
                          {
                            value: CREATE_NEW_OPTION,
                            label: "+ Создать новый вид работ",
                          },
                        ]);
                      } else {
                        setAddWorkTypeSelected(selected);
                      }
                    }}
                    isLoading={workTypeNamesLoading}
                    isDisabled={addWorkTypeSubmitting}
                    placeholder="Начните вводить или выберите из списка..."
                    noOptionsMessage={() => "Виды работ не найдены"}
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                  />
                </>
              )}
            </div>
            {isCreateNew && (
              <div className="sd-estimate-modal-field">
                <label>
                  Введите название вида работ, которого нет в списке
                </label>
                <span className="sd-estimate-display-row">
                  <input
                    type="text"
                    className="sd-estimate-modal-input"
                    value={addWorkTypeNewName}
                    onChange={(e) => setAddWorkTypeNewName(e.target.value)}
                    onBlur={validateNewName}
                    placeholder="Введите название вида работ"
                  />
                  <button
                    type="button"
                    className="sd-estimate-pencil"
                    aria-label="Редактировать"
                  >
                    ✎
                  </button>
                </span>
                {isDuplicateName && (
                  <p className="sd-estimate-modal-error">
                    <span className="sd-estimate-modal-error-icon" aria-hidden>
                      !
                    </span>
                    Такое название уже существует! Выберите его из списка.
                  </p>
                )}
                {isCreateNew && newNameTrimmed && !isDuplicateName && (
                  <p className="sd-estimate-modal-success">
                    <span
                      className="sd-estimate-modal-success-icon"
                      aria-hidden
                    >
                      ✓
                    </span>
                  </p>
                )}
              </div>
            )}
            <div className="sd-estimate-modal-actions">
              <button
                type="button"
                className="sd-estimate-modal-btn-cancel"
                onClick={closeAddModal}
              >
                Отмена
              </button>
              <button
                type="button"
                className="sd-estimate-modal-btn-add"
                onClick={handleAddWorkType}
                disabled={!canAddWorkType || addWorkTypeSubmitting}
              >
                {addWorkTypeSubmitting ? "Добавление…" : "Добавить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Блок действий согласующего: Согласовать / Отклонить (если я в списке и статус «на рассмотрении») */}
      {isMyApprovalPending && (
        <div className="sd-estimate-approver-actions">
          <span className="sd-estimate-approver-actions__label">
            Ваше решение по согласованию:
          </span>
          <div className="sd-estimate-approver-actions__buttons">
            <button
              type="button"
              className="sd-estimate-approver-actions__btn sd-estimate-approver-actions__btn--approve"
              onClick={() => handleApproverSetStatus("approved")}
              disabled={isApproverActionLoading}
            >
              {isApproverActionLoading ? "..." : "Согласовать"}
            </button>
            <button
              type="button"
              className="sd-estimate-approver-actions__btn sd-estimate-approver-actions__btn--reject"
              onClick={() => handleApproverSetStatus("rejected")}
              disabled={isApproverActionLoading}
            >
              Отклонить
            </button>
          </div>
        </div>
      )}

      <CommercialOfferApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        projectData={projectData}
        isSubmitting={isSubmittingApproval}
        title="Согласование сметы (ЭП)"
        warningText="Подтвердите отправку сметы на согласование."
        allowAddFromUserList
      />
    </div>
  );
};

export default Estimate;
